import React, { useCallback, useRef } from "react";

import {
  TransitionSelectItems,
  CompactNumberInput,
} from "@/components/slides/presentation-settings";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSlideStore } from "@/stores/slide-store";
import type { SlideInfo, TransitionType } from "@/stores/slide-store";
import { claudeAgentApi } from "@/lib/api/claude-agent";
import { previewMessage } from "@/lib/visual-editor/post-message";

interface SlideSettingsProps {
  slide: SlideInfo;
  previewIframeRef: React.RefObject<HTMLIFrameElement | null>;
  devContainerId?: string;
  getIdToken: () => Promise<string | null>;
  children: React.ReactNode;
}

export function SlideSettings({
  slide,
  previewIframeRef,
  devContainerId,
  getIdToken,
  children,
}: SlideSettingsProps) {
  const { presentationConfig, updateSlideConfig } = useSlideStore();
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const debouncedSave = useCallback(
    (config: {
      interval?: number | null;
      transition?: TransitionType | null;
      transitionDuration?: number | null;
    }) => {
      updateSlideConfig(slide.id, config);

      // Send to preview iframe for instant feedback
      previewMessage.updatePerSlideConfig(
        previewIframeRef.current,
        slide.id,
        config as Record<string, boolean | number | string | null>,
      );

      // Debounce save to sidecar
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(async () => {
        if (!devContainerId) return;
        try {
          const token = await getIdToken();
          if (!token) return;
          const freshSlide = useSlideStore
            .getState()
            .slides.find((s) => s.id === slide.id);
          if (!freshSlide) return;
          await claudeAgentApi.updateSlideConfig(
            token,
            devContainerId,
            slide.id,
            {
              interval: freshSlide.interval,
              transition: freshSlide.transition,
              transitionDuration: freshSlide.transitionDuration,
            },
          );
        } catch (err) {
          console.error("Failed to save slide config:", err);
        }
      }, 500);
    },
    [slide.id, devContainerId, getIdToken, updateSlideConfig, previewIframeRef],
  );

  const hasIntervalOverride = slide.interval != null;
  const hasTransitionOverride = slide.transition != null;
  const hasDurationOverride = slide.transitionDuration != null;

  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent side="right" align="start" className="w-56 p-0">
        <div className="p-3 space-y-2.5">
          <p className="text-xs font-medium text-foreground">{slide.title}</p>

          {/* Interval */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-normal">Interval</Label>
              <CompactNumberInput
                value={
                  hasIntervalOverride
                    ? slide.interval! / 1000
                    : presentationConfig.interval / 1000
                }
                onChange={(val) => debouncedSave({ interval: Math.round(val * 1000) })}
                min={1}
                max={60}
                step={1}
                unit="s"
                disabled={!hasIntervalOverride}
              />
            </div>
            <div className="flex items-center gap-1.5">
              <Switch
                checked={!hasIntervalOverride}
                onCheckedChange={(checked: boolean) => {
                  if (checked) {
                    debouncedSave({ interval: null });
                  } else {
                    debouncedSave({ interval: presentationConfig.interval });
                  }
                }}
                className="scale-[0.6] origin-left"
              />
              <span className="text-[10px] text-muted-foreground">
                Use global
              </span>
            </div>
          </div>

          <div className="border-t border-border" />

          {/* Transition */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-normal">Transition</Label>
              <Select
                value={
                  hasTransitionOverride
                    ? slide.transition!
                    : presentationConfig.transition
                }
                onValueChange={(value: string) => {
                  debouncedSave({ transition: value as TransitionType });
                }}
                disabled={!hasTransitionOverride}
              >
                <SelectTrigger className="h-7 w-24 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <TransitionSelectItems />
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1.5">
              <Switch
                checked={!hasTransitionOverride}
                onCheckedChange={(checked: boolean) => {
                  if (checked) {
                    debouncedSave({ transition: null });
                  } else {
                    debouncedSave({
                      transition: presentationConfig.transition,
                    });
                  }
                }}
                className="scale-[0.6] origin-left"
              />
              <span className="text-[10px] text-muted-foreground">
                Use global
              </span>
            </div>
          </div>

          <div className="border-t border-border" />

          {/* Duration */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-normal">Duration</Label>
              <CompactNumberInput
                value={
                  hasDurationOverride
                    ? slide.transitionDuration!
                    : presentationConfig.transitionDuration
                }
                onChange={(val) => debouncedSave({ transitionDuration: val })}
                min={0}
                max={2000}
                step={50}
                unit="ms"
                disabled={!hasDurationOverride}
              />
            </div>
            <div className="flex items-center gap-1.5">
              <Switch
                checked={!hasDurationOverride}
                onCheckedChange={(checked: boolean) => {
                  if (checked) {
                    debouncedSave({ transitionDuration: null });
                  } else {
                    debouncedSave({
                      transitionDuration:
                        presentationConfig.transitionDuration,
                    });
                  }
                }}
                className="scale-[0.6] origin-left"
              />
              <span className="text-[10px] text-muted-foreground">
                Use global
              </span>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
